<template>
  <div class="bf-form-success" v-if="successMessage || isAlreadySubscribed">
    <span class="bf-form-title">{{ $t('bf-form-success-title') }}</span>
    <span class="bf-form-subtitle">{{ $t('bf-form-success-subtitle') }}</span>
  </div>
  <form v-else class="bf-form" @submit.prevent="submitForm">
    <div class="bf-form__wrapper">
      <div class="row">
        <template v-if="isUserAuth">
          <div class="col">
            <div class="control-box" :class="{ 'invalid dirty': $v.formData.name.$error }">
              <custom-input
                id="bf-name"
                v-model.trim="formData.name"
                :placeholder="$t('subscribe.placeholder.name')"
                @onBlur="$v.formData.name.$touch"
              ></custom-input>
              <div class="bf-form__errors-wrap">
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.name.$error }" v-if="$v.formData.name.$error">
                  <template v-if="!$v.formData.name.required">
                    {{ $t('blank_field_error') }}
                  </template>
                  <template v-else-if="!$v.formData.name.minLength">
                    {{ $t('notfull_field_error') }}
                  </template>
                  <template v-else-if="!$v.formData.name.regRus">
                    {{ $t('name_contains_rus_letters') }}
                  </template>
                  <template v-else-if="!$v.formData.name.regNum">
                    {{ $t('field_contains_numbers') }}
                  </template>
                  <template v-else-if="!$v.formData.name.regSpace">
                    {{ $t('field_contains_space') }}
                  </template>
                  <template v-else-if="!$v.formData.name.regSpec">
                    {{ $t('field_contains_special_characters') }}
                  </template>
                  <template v-else-if="!$v.formData.name.regLatin">
                    {{ $t('field_contains_latin_letter') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="control-box" :class="{ 'invalid dirty': $v.formData.email.$error }">
              <custom-input
                id="bf-email"
                v-model.trim="formData.email"
                :placeholder="$t('email_placeholder')"
                @onBlur="$v.formData.email.$touch"
              ></custom-input>
              <div class="bf-form__errors-wrap">
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.email.$error }" v-if="$v.formData.email.$error">
                  <template v-if="!$v.formData.email.required">
                    {{ $t('blank_field_error') }}
                  </template>
                  <template v-else-if="!$v.formData.email.email">
                    {{ $t('subscribe_error') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
          <div class="col col--full">
            <div class="control-box" :class="{ 'invalid dirty': $v.formData.phone.$error }">
              <phone-input
                class="bf-form__phone-input"
                v-model.trim="formData.phone"
                disable-fields
              ></phone-input>
              <div class="bf-form__errors-wrap">
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.phone.$error }" v-if="$v.formData.phone.$error">
                  <template v-if="!$v.formData.phone.required">
                    {{ $t('blank_field_error') }}
                  </template>
                  <template v-else-if="!$v.formData.phone.phoneValidator">
                    {{ $t('only_polish_phone_msg') }}
                  </template>
                  <template v-else-if="!$v.formData.phone.minLength">
                    {{ $t('notfull_field_error') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="col col--full">
          <auth-phone
            :rememberField="false"
            :touch-phone-flag="touchPhoneFlag"
            is-hidden-labels
            is-ajax-auth
          ></auth-phone>
        </div>
        <div class="col">
          <custom-button
            nativeType="submit"
            :is-native-disabled="isLoading"
            class="bf-form__btn"
          >
            {{ $t('bf-form-btn-text') }}
            <custom-loader v-if="isLoading" :border-size="5" is-loading></custom-loader>
          </custom-button>
        </div>
        <div class="col">
          <div class="bf-form__agreement">
            {{ $t('bf-form-agreement') }}
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api'

import CustomInput from '@/components/inputs/Input'
import AuthPhone from '@/components/auth/AuthPhone'
import PhoneInput from '@/components/inputs/PhoneInput'
import { minLength, required, email, helpers } from 'vuelidate/lib/validators'
import CustomButton from '@/components/inputs/Button'
import CustomLoader from '@/components/helpers/Loader'
import { phoneValidations } from '@/helpers/phoneValidations'

const regRus = value => !new RegExp(/[ъыёэ]/i).test(value)
const regLatin = value => !new RegExp(/[a-z]/i).test(value)
const regSpace = value => !new RegExp(/\s/g).test(value)
const regNum = value => !new RegExp(/\d/).test(value)
const regSpec = value => !new RegExp('[!@#№/$%^&*(),.?":;_=+{}|<>]').test(value)
const phoneValidator = helpers.regex(
  'phoneValidator',
  phoneValidations
)

export default {
  name: 'BlackFridayForm',
  components: {
    CustomLoader,
    CustomButton,
    PhoneInput,
    CustomInput,
    AuthPhone
  },
  computed: {
    ...mapState({
      userFromServer: state => state.user
    }),
    isUserAuth () {
      return this.userFromServer?.auth
    },
    userData () {
      return this.userFromServer?.['user_data']
    },
    isAlreadySubscribed () {
      return !!+this.userData?.subscriber?.['black_friday']
    },
    isValidForm () {
      return !this.$v.formData.$error && !this.$v.formData.$invalid
    }
  },
  props: {
    target: {
      type: String,
      validator: val => ['page', 'page-bottom', 'modal'].includes(val)
    }
  },
  data () {
    return {
      formData: {
        name: '',
        email: '',
        phone: ''
      },
      touchPhoneFlag: '',
      isLoading: false,
      successMessage: ''
    }
  },
  validations: {
    formData: {
      name: {
        required,
        minLength: minLength(2),
        regRus,
        regLatin,
        regSpace,
        regNum,
        regSpec
      },
      email: {
        required,
        email
      },
      phone: {
        required,
        phoneValidator,
        minLength: minLength(9)
      }
    }
  },
  methods: {
    touchAuthPhone () {
      this.touchPhoneFlag = new Date().getTime().toString()
    },
    touchForm () {
      if (!this.isUserAuth) {
        this.touchAuthPhone()
      } else {
        this.$v.formData.$touch()
      }
    },
    submitForm () {
      this.touchForm()

      if (this.isValidForm) {
        this.isLoading = true

        const data = {
          ...this.formData,
          phone: '380' + this.formData.phone,
          target: this.target
        }

        api.landingSubscribe.subscribe('/black-friday/subscribe/', data)
          .then(res => {
            if (res && res.success) {
              this.$emit('submit')
              this.successMessage = res.message
              this.$store.commit('UPDATE_USER_DATA', { ...res['user_data'], subscriber: { ...this.userData.subscriber, 'black_friday': 1 } })
            }
          })
          .catch(error => {
            this.$notify({
              group: 'error',
              title: this.$t('error'),
              text: error,
              type: 'error'
            })
          })
          // eslint-disable-next-line no-return-assign
          .finally(() => this.isLoading = false)
      }
    }
  },
  watch: {
    userData: {
      immediate: true,
      handler (val) {
        this.formData.phone = val?.phone
        this.formData.name = val?.name
        this.formData.email = val?.email
      }
    }
  }
}
</script>

<style scoped lang="sass">
::v-deep
  .control-box
    margin-bottom: 0

  .control
    background-color: rgba(255,255,255,.2)
    border-color: transparent
    color: #fff

    &::placeholder
      color: #fff

    &:focus
      border-color: rgba(255,255,255,.2)

    &:disabled
      background-color: rgba(255,255,255,.3)

  .control-box.control-box--phone
    width: 100%
    margin-right: 0

    +mmin($tablet)
      width: calc(50% - 7px)
      margin-right: 14px

  .control-box.control-box--code
    width: 150px
    margin-top: 20px

    +mmin($tablet)
      margin-top: 0

  .control--code-auth
    max-width: 150px

  .form-group
    margin-bottom: 0

.row
  display: flex
  flex-wrap: wrap
  margin-left: -7px
  margin-right: -7px

  .col
    padding: 0 7px
    flex: 0 0 100%
    width: 50%
    margin-bottom: 20px

    +mmin($tablet)
      flex: 0 0 50%

    &--full
      flex: 0 0 100%
      width: 100%

.bf-form__wrapper
  max-width: 674px

.bf-form__btn
  filter: drop-shadow(0px 12px 30px rgba(255, 47, 0, 0.5))
  width: 100%

  +mmin($tablet)
    max-width: 330px

  &:hover
    background-color: $main-color--hover

.bf-form__agreement
  color: #909090
  font-size: 13px
  font-weight: 500
  line-height: 1.5

.bf-form__phone-input
  width: 100%

  +mmin($tablet)
    width: calc(50% - 7px)

.bf-form-success
  margin: 80px 0 50px

.bf-form-title
  display: block
  color: #E7E7E7
  font-weight: 500
  font-size: 24px
  text-align: center
  letter-spacing: 0.05em
  text-transform: uppercase
  margin-bottom: 15px

  +mmin($tablet)
    font-size: 36px

.bf-form-subtitle
  display: block
  color: #E7E7E7
  font-weight: 500
  font-size: 14px
  line-height: 150%
  text-align: center
  letter-spacing: 0.05em
  text-transform: uppercase

  +mmin($tablet)
    font-size: 18px

</style>
